import React from "react";
import { Document, Page, View, StyleSheet, Font } from "@react-pdf/renderer";
import { format } from "date-fns";
import { INTERNATIONAL_DATE_FORMAT } from "src/components/constants/Dates";
import {
    FONT_OPEN_SANS_BOLD,
    FONT_OPEN_SANS_REGULAR,
} from "src/components/constants";
import FirstPageContent from "./FirstPage/first-page";
import FirstHeader from "./FirstPage/firstHeader";
import FirstFooter from "./FirstPage/firstFooter";
import PageContent from "./OtherPages/page-content";
import Header from "./OtherPages/header";
import mapConformityExportToPdf from "./helpers/mapConformityExportToPdf";

Font.register({
    family: "Open Sans",
    fonts: [
        {
            src: FONT_OPEN_SANS_REGULAR,
            fontWeight: 400,
            fontStyle: "normal",
        },
        {
            src: FONT_OPEN_SANS_BOLD,
            fontWeight: 600,
            fontStyle: "bold",
        },
    ],
});

const styles = StyleSheet.create({
    page: {
        fontFamily: "Open Sans",
        flexDirection: "column",
        backgroundColor: "#FFFFFF",
        paddingBottom: 40,
    },
});

const generateColor = (hexColor, magnitude) => {
    hexColor = hexColor.replace(`#`, ``);
    if (hexColor?.length === 6) {
        const decimalColor = parseInt(hexColor, 16);
        let r = (decimalColor >> 16) + magnitude;
        r > 255 && (r = 255);
        r < 0 && (r = 0);
        let g = (decimalColor & 0x0000ff) + magnitude;
        g > 255 && (g = 255);
        g < 0 && (g = 0);
        let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
        b > 255 && (b = 255);
        b < 0 && (b = 0);
        return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
    }

    return hexColor;
};

const formatExportedAt = (exportedAt) => {
    if (!exportedAt) {
        return null;
    }

    try {
        const date = new Date(exportedAt);

        if (Number.isNaN(date.getTime())) {
            return String(exportedAt);
        }

        return format(date, INTERNATIONAL_DATE_FORMAT);
    } catch (error) {
        return String(exportedAt);
    }
};

const CeConformityPdfExport = ({ data, organization, user }) => {
    const org = organization || {};
    const mapped = mapConformityExportToPdf(data || {});

    const logo = {
        src: null,
        size: {
            maxWidth: "240px",
            maxHeight: "120px",
            height: "40px",
        },
    };

    if (org.secondaryLogo) {
        logo.src = org.secondaryLogo;
        logo.size = {
            maxWidth: "200px",
            height: `${org.secondaryLogoSize || 40}px`,
        };
    } else if (org.primaryLogo) {
        logo.src = org.primaryLogo;
        logo.size = {
            maxWidth: "240px",
            maxHeight: "120px",
            height: `${org.primaryLogoSize || 40}px`,
        };
    }

    const primaryColor = org.primaryColor || "#5664d2";
    const startColor = primaryColor;
    const stopColor = generateColor(String(primaryColor), 15);
    const exportedAt =
        formatExportedAt(mapped.exportedAt) ||
        format(new Date(), INTERNATIONAL_DATE_FORMAT);

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <FirstHeader
                    startColor={startColor}
                    stopColor={stopColor}
                    backgroundColor={primaryColor}
                    logo={logo}
                />
                <View>
                    <FirstPageContent
                        exportedAt={exportedAt}
                        user={user}
                        cover={mapped.cover}
                    />
                </View>
                <FirstFooter backgroundColor={primaryColor} />
            </Page>

            <Page size="A4" style={styles.page}>
                <Header
                    startColor={startColor}
                    stopColor={stopColor}
                    backgroundColor={primaryColor}
                    logo={logo}
                />
                <PageContent mapped={mapped} />
                <FirstFooter backgroundColor={primaryColor} />
            </Page>
        </Document>
    );
};

export default CeConformityPdfExport;
